import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { Box, IconButton, Stack, Switch } from "@mui/material";
import { FormikHelpers } from "formik";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { ConfirmationDialog, CustomDataGrid } from "@shared/components";
import {
  formatDateTime,
  getActionPermission,
  getCapitalizeString,
} from "@shared/utils/functions";
import { useGetAllInvoiceParties } from "../services/useGetInvoiceParties";
import { AllowedSortFields } from "../utils/constants";
import {
  IInvoiceRow,
  InvoiceDataGridProps,
  InvoiceFormValues,
  SortField,
} from "../utils/types";
import InvoiceForm from "./InvoiceForm";
import { useUpdateInvoiceParty } from "../services/useUpdateInvoiceParty";
import { useDeleteInvoiceParty } from "../services/useDeleteInvoiceParty";
import { useSortModel } from "@shared/components/grid-components/hooks/useSortModel";
import { TableActions } from "@shared/utils/constants";
import { Can, useCan } from "@core/casl-provider";

const InvoiceDataGrid = ({
  visibleColumns,
  setPaginationModel,
  paginationModel,
}: InvoiceDataGridProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("searchQuery") ?? "";
  const updatedSince = searchParams.get("updatedSince") ?? "";
  const ability = useCan();

  const [actionState, setActionState] = useState<{
    action: "EDIT" | "DELETE" | "";
    payload: Partial<InvoiceFormValues> & { id?: number; name?: string };
  }>({
    action: "",
    payload: {},
  });

  const [pendingToggle, setPendingToggle] = useState<{
    id: IInvoiceRow["id"];
    field: "activityState" | "campaignLimitedDataTransfer";
    newValue: boolean;
    name?: string;
  } | null>(null);

  const { sortModel, handleSortModelChange, orderBy, order } =
    useSortModel<SortField>({
      allowedSortFields: AllowedSortFields,
    });

  const { data: invoicePartiesList, isLoading } = useGetAllInvoiceParties({
    data: {
      pageNumber: String(paginationModel.page + 1),
      pageSize: String(paginationModel.pageSize),
      includeInactive: true,
      // Map unified searchQuery to titleName for backend filtering compatibility
      searchQuery: searchQuery,
      orderBy: orderBy,
      order: order,
      updatedSince: updatedSince,
    },
  });

  const { mutate: deleteMutation } = useDeleteInvoiceParty();
  const {
    mutate: updateFormMutation,
    mutateAsync: updateFormMutationAsync,
    isPending: isFormPending,
  } = useUpdateInvoiceParty();

  const [allConfigurations, setAllConfigurations] = useState<IInvoiceRow[]>();

  const totalRows: number =
    (invoicePartiesList?.pagination?.total as number) ??
    allConfigurations?.length ??
    0;

  const clearActionState = () => setActionState({ action: "", payload: {} });

  const handleDeleteConfirm = async () => {
    deleteMutation({ id: String(actionState.payload.id) });
    clearActionState();
  };

  const handleEditSubmit = async (
    values: InvoiceFormValues & { id?: number },
    helpers: FormikHelpers<InvoiceFormValues & { id?: number }>
  ) => {
    try {
      const id = actionState.payload.id;
      const payloadData = { ...values };
      delete payloadData.id;

      await updateFormMutationAsync({
        payload: { id: id as number, payloadData },
      });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error
          ? error.message
          : "Failed to update configuration";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
        preventDuplicate: true,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const columns: GridColDef<IInvoiceRow>[] = [
    {
      field: "uid",
      headerName: "UID",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<IInvoiceRow, string | number>
      ) => <span>{params.value}</span>,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IInvoiceRow, string>) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "company",
      headerName: "Company",
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: "emailAddress",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "account",
      headerName: "Account",
      flex: 0.2,
      minWidth: 120,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IInvoiceRow, number>) => (
        <span>{params.value}</span>
      ),
    },
    {
      field: "activityState",
      headerName: "Is Active",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<IInvoiceRow, boolean>) => (
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent={"start"}
          height={"100%"}
        >
          <Switch
            checked={Boolean(params.value)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => {
              if (!ability.can("update", "InvoiceParty")) {
                enqueueSnackbar(
                  "You don't have permission to update invoice parties",
                  { variant: "error" }
                );
                return;
              }
              setPendingToggle({
                id: params.row.id,
                field: "activityState",
                newValue: event.target.checked,
                name: params.row.name,
              });
            }}
          />
        </Stack>
      ),
    },
    {
      field: "campaignLimitedDataTransfer",
      headerName: "Campaign Limit Data Transfer",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<IInvoiceRow, boolean>) => (
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent={"start"}
          height={"100%"}
        >
          <Switch
            checked={Boolean(params.value)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => {
              setPendingToggle({
                id: params.row.id,
                field: "campaignLimitedDataTransfer",
                newValue: event.target.checked,
                name: params.row.name,
              });
            }}
          />
        </Stack>
      ),
    },
    {
      field: "comment",
      headerName: "Comment",
      flex: 1,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IInvoiceRow, string>) =>
        formatDateTime(params.value),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (
        params: GridRenderCellParams<
          IInvoiceRow,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >
      ) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          sx={{ height: "100%" }}
        >
          {TableActions.map((action, index) => {
            const actionPermission = getActionPermission(action);

            return (
              <Can
                do={actionPermission}
                on="InvoiceParty"
                key={index}
              >
                <IconButton
                  title={action.label}
                  style={{
                    backgroundColor: "primary.main",
                  }}
                  onClick={() =>
                    setActionState({
                      action: action.key,
                      payload: {
                        id: params.row.id,
                        name: params.row.name,
                        emailAddress: params.row.emailAddress,
                        company: params.row.company,
                        account: params.row.account,
                        campaignLimitedDataTransfer:
                          params.row.campaignLimitedDataTransfer,
                        comment: params.row.comment,
                        activityState: params.row.activityState,
                      },
                    })
                  }
                >
                  {action.icon}
                </IconButton>
              </Can>
            );
          })}
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    if (invoicePartiesList?.data) {
      const data = invoicePartiesList.data.map((item) => ({
        ...item,
        id: item.uid,
      }));
      setAllConfigurations(data as IInvoiceRow[]);
    }
  }, [invoicePartiesList]);

  const handleSwitchChange = useCallback(
    (
      id: IInvoiceRow["id"],
      field: "activityState" | "campaignLimitedDataTransfer",
      newValue: boolean
    ) => {
      // 1. Optimistic Update (Update the UI immediately)
      const originalConfigurations = allConfigurations;
      setAllConfigurations((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row))
      );

      // 2. Prepare API payload
      const payloadData: Partial<IInvoiceRow> = {
        [field]: newValue,
      };

      // 3. Call Update API
      updateFormMutation(
        { payload: { id: id, payloadData } },
        {
          onError: (error: Error) => {
            // Revert the state on API failure (Pessimistic update after failure)
            setAllConfigurations(originalConfigurations);
            enqueueSnackbar(`Failed to update ${field}: ${error.message}`, {
              variant: "error",
              autoHideDuration: 3000,
              preventDuplicate: true,
            });
          },
        }
      );
    },
    [allConfigurations, updateFormMutation]
  );

  const handlePendingToggleConfirm = () => {
    if (pendingToggle) {
      handleSwitchChange(
        pendingToggle.id,
        pendingToggle.field,
        pendingToggle.newValue
      );
    }
    setPendingToggle(null);
  };

  const getConfirmationTitle = (toggle: typeof pendingToggle): string => {
    if (!toggle) return "Confirm";
    switch (toggle.field) {
      case "activityState":
        return "Confirm Activity State";
      case "campaignLimitedDataTransfer":
        return "Confirm Campaign Limited Data Transfer";
      default:
        return "Confirm";
    }
  };

  const getConfirmationMessage = (toggle?: typeof pendingToggle): string => {
    if (!toggle) return "Confirm";

    const name = getCapitalizeString(toggle.name);
    const action = toggle.newValue ? "enable" : "disable";

    const fieldMessages: Record<string, string> = {
      activityState: "activity state",
      campaignLimitedDataTransfer: "campaign limited data transfer",
    };

    const fieldLabel = fieldMessages[toggle.field];
    if (!fieldLabel) return "Confirm";

    return `Are you sure you want to ${action} ${fieldLabel} for "${name}"?`;
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
          overflow: "hidden",
        })}
      >
        <CustomDataGrid<IInvoiceRow>
          rows={allConfigurations}
          columns={columns}
          visibleColumns={visibleColumns}
          getRowId={(row) => row.id}
          rowCount={totalRows}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          autoHeight
          disableSelectionOnClick
          loading={isLoading}
          dataGridSx={(theme) => ({
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          })}
        />
      </Box>

      <ConfirmationDialog
        open={!!pendingToggle}
        onClose={() => setPendingToggle(null)}
        onConfirm={handlePendingToggleConfirm}
        title={getConfirmationTitle(pendingToggle)}
        message={getConfirmationMessage(pendingToggle)}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmColor="primary"
      />

      <ConfirmationDialog
        open={actionState.action === "DELETE"}
        onClose={clearActionState}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${actionState.payload.name ?? "Invoice Party"}`}
        message="Are you sure you want to Delete?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="primary"
      />
      <InvoiceForm
        open={actionState.action === "EDIT"}
        onClose={clearActionState}
        title={"Edit Invoice Party"}
        initialValues={actionState.payload}
        disable={isFormPending}
        onSubmit={handleEditSubmit}
        showCloseButton
      />
    </>
  );
};

export default InvoiceDataGrid;
